import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ==============================================================================
// >> CONFIG & STYLES
// ==============================================================================
const API_BASE_URL = "https://qf-v1.onrender.com";

const styles = {
  container: { minHeight: '100vh', background: '#f3f4f6', padding: '2rem', fontFamily: 'sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', background: '#fff', padding: '1rem 2rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  title: { fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: 0 },
  
  // Controls Area
  controls: { display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  input: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #d1d5db', minWidth: '200px' },
  select: { padding: '0.5rem 2rem 0.5rem 1rem', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff' },
  
  // Table
  tableCard: { background: '#fff', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  th: { padding: '1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#374151', fontSize: '0.85rem', textTransform: 'uppercase' },
  td: { padding: '1rem', borderBottom: '1px solid #e5e7eb', color: '#111827', fontSize: '0.95rem', verticalAlign: 'middle' },
  imgThumb: { width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #eee' },
  
  // Status Pills (Updated for inventory_status)
  pill: (status) => {
    let bg = '#e5e7eb'; let color = '#374151';
    if (status === 'live') { bg = '#dcfce7'; color = '#166534'; }
    if (status === 'staging') { bg = '#fef3c7'; color = '#92400e'; }
    if (status === 'out_of_stock') { bg = '#fee2e2'; color = '#991b1b'; }
    if (status === 'created_manually') { bg = '#dbeafe'; color = '#1e40af'; }
    return { display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', background: bg, color: color, textTransform: 'capitalize' };
  },

  // Buttons
  btn: { padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '500', transition: '0.2s' },
  btnPrimary: { background: '#f43f5e', color: '#fff' },
  btnSecondary: { background: '#fff', border: '1px solid #d1d5db', color: '#374151' },
  btnDanger: { background: '#fee2e2', color: '#dc2626' },
  
  // Modal
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '2rem 0' },
  modal: { background: '#fff', width: '800px', maxWidth: '95%', borderRadius: '12px', padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', maxHeight: '90vh', overflowY: 'auto' },
  formGroup: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' },
  formInput: { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.95rem' },
  textarea: { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.95rem', minHeight: '80px', fontFamily: 'inherit' },
  
  // Color Angles Grid
  anglesCard: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }
};

// ==============================================================================
// >> COMPONENT
// ==============================================================================

const VADashboard = () => {
  const navigate = useNavigate();

  // --- STATE ---
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  
  // Filters & Sorting (Updated to use inventory_status)
  const [filters, setFilters] = useState({ brand: '', inventory_status: '', search: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'updated_at', order: 'desc' });
  
  // Selection & Editing
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editItem, setEditItem] = useState(null); 
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [newAngleColor, setNewAngleColor] = useState(''); 

  // --- FETCH DATA ---
  const fetchProducts = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 50,
        ...(filters.brand && { brand: filters.brand }),
        ...(filters.inventory_status && { inventory_status: filters.inventory_status }), // Updated
        ...(filters.search && { search: filters.search }),
        sort_by: sortConfig.field,
        sort_order: sortConfig.order,
      });

      const res = await fetch(`${API_BASE_URL}/va/products?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.status === 403) { alert("Access Denied"); navigate('/'); return; }
      
      const data = await res.json();
      setProducts(data.data);
      setPagination(data.pagination);
    } catch (err) {
      console.error(err);
      alert("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line
  }, [pagination.page, filters, sortConfig]);

  // --- HANDLERS ---

  const handleFilterChange = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
    setPagination(prev => ({ ...prev, page: 1 })); 
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const handleBatchStatus = async (newStatus) => {
    const token = localStorage.getItem('token');
    if (selectedIds.size === 0) return;

    if (!window.confirm(`Mark ${selectedIds.size} items as ${newStatus}?`)) return;

    try {
      await fetch(`${API_BASE_URL}/va/products/batch-status`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: Array.from(selectedIds),
          inventory_status: newStatus // Updated
        })
      });
      
      setSelectedIds(new Set());
      fetchProducts();
    } catch (err) {
      alert("Batch update failed");
    }
  };

  const openEditModal = async (brand, title) => {
    setIsFetchingDetails(true);
    setNewAngleColor(''); 
    const token = localStorage.getItem('token');
    
    try {
      const params = new URLSearchParams({ brand, title });
      const res = await fetch(`${API_BASE_URL}/va/products/detail?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to load product details");
      
      const data = await res.json();
      if (!data.color_angles) data.color_angles = {};
      setEditItem(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleColorAngleChange = (colorKey, angle, val) => {
    setEditItem(prev => ({
      ...prev,
      color_angles: {
        ...prev.color_angles,
        [colorKey]: {
          ...(prev.color_angles[colorKey] || {}),
          [angle]: val
        }
      }
    }));
  };

  const handleRemoveColorAngle = (colorKey) => {
    const updatedAngles = { ...editItem.color_angles };
    delete updatedAngles[colorKey];
    setEditItem(prev => ({ ...prev, color_angles: updatedAngles }));
  };

  const handleAddColorAngle = () => {
    const color = newAngleColor.trim();
    if (color && (!editItem.color_angles || !editItem.color_angles[color])) {
      setEditItem(prev => ({
        ...prev,
        color_angles: {
          ...prev.color_angles,
          [color]: { front: '', side: '', back: '' }
        }
      }));
      setNewAngleColor('');
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const payload = {
      ...editItem,
      sizes: typeof editItem.sizes === 'string' ? editItem.sizes.split(',').map(s => s.trim()) : editItem.sizes,
      colors: typeof editItem.colors === 'string' ? editItem.colors.split(',').map(s => s.trim()) : editItem.colors,
      available_stores: typeof editItem.available_stores === 'string' ? editItem.available_stores.split(',').map(s => s.trim()) : editItem.available_stores,
    };

    try {
      const url = editItem._id 
        ? `${API_BASE_URL}/va/products/detail` 
        : `${API_BASE_URL}/va/products`;
      
      const method = editItem._id ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Failed to save");
      
      setEditItem(null);
      fetchProducts();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product? This cannot be undone.")) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_BASE_URL}/va/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchProducts();
    } catch (err) {
      alert("Delete failed");
    }
  };

  // --- RENDER ---

  return (
    <div style={styles.container}>
      
      {/* Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Virtual Assistant Dashboard</h1>
          <p style={{color: '#6b7280', fontSize: '0.9rem', marginTop: '4px'}}>Reviewing Collection: <strong>products_11_21</strong></p>
        </div>
        <div style={{display: 'flex', gap: '1rem'}}>
          <button style={{...styles.btn, ...styles.btnSecondary}} onClick={() => {
            setEditItem({ inventory_status: 'staging', sizes: '', colors: '', color_angles: {} });
            setNewAngleColor('');
          }}>
            + Add Manual Item
          </button>
          <button style={{...styles.btn, ...styles.btnSecondary}} onClick={() => { localStorage.removeItem('token'); navigate('/login'); }}>
            Logout
          </button>
        </div>
      </header>

      {/* Controls */}
      <div style={styles.controls}>
        <input 
          style={styles.input} 
          placeholder="Search title..." 
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
        />
        <select style={styles.select} value={filters.brand} onChange={(e) => handleFilterChange('brand', e.target.value)}>
          <option value="">All Brands</option>
          <option value="Brandy Melville">Brandy Melville</option>
          <option value="Zara">Zara</option>
          <option value="Skims">Skims</option>
          <option value="withjean">withjean</option>
          <option value="Guizio">Guizio</option>
          <option value="Aritzia">Aritzia</option>
          <option value="Alo">Alo</option>
          <option value="Banana Republic">Banana Republic</option>
          <option value="Princess Polly">Princess Polly</option>
          <option value="Victoria's Secret">Victoria's Secret</option>
          <option value="Reformation">Reformation</option>
          <option value="Theory">Theory</option>
          <option value="Vince">Vince</option>
          <option value="Asta Resort">Asta Resort</option>
          <option value="Edikted">Edikted</option>
          <option value="Lululemon">Lululemon</option>
          <option value="Uniqlo">Uniqlo</option>
          <option value="Peter Millar">Peter Millar</option>
          <option value="On">On</option>
        </select>
        <select style={styles.select} value={filters.inventory_status} onChange={(e) => handleFilterChange('inventory_status', e.target.value)}>
          <option value="">All Statuses</option>
          <option value="staging">Staging</option>
          <option value="live">Live</option>
          <option value="out_of_stock">Out of Stock</option>
        </select>
        
        {selectedIds.size > 0 && (
           <button style={{...styles.btn, ...styles.btnPrimary, marginLeft: 'auto'}} onClick={() => handleBatchStatus('live')}>
             Mark {selectedIds.size} Live
           </button>
        )}
      </div>

      {/* Data Table */}
      <div style={styles.tableCard}>
        {loading ? (
          <div style={{padding: '2rem', textAlign: 'center', color: '#6b7280'}}>Loading staging data...</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>
                  <input 
                    type="checkbox" 
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(new Set(products.map(p => p._id)));
                      else setSelectedIds(new Set());
                    }}
                    checked={products.length > 0 && selectedIds.size === products.length}
                  />
                </th>
                <th style={styles.th}>Image</th>
                <th style={styles.th}>Details</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Inventory Status</th>
                <th style={styles.th}>Flags</th> {/* ADD THIS LINE */}
                <th 
                  style={{...styles.th, cursor: 'pointer', userSelect: 'none'}} 
                  onClick={() => handleSort('created_at')}
                >
                  Added Date {sortConfig.field === 'created_at' ? (sortConfig.order === 'desc' ? '▼' : '▲') : ''}
                </th>
                <th 
                  style={{...styles.th, cursor: 'pointer', userSelect: 'none'}} 
                  onClick={() => handleSort('updated_at')}
                >
                  Updated Date {sortConfig.field === 'updated_at' ? (sortConfig.order === 'desc' ? '▼' : '▲') : ''}
                </th>
                <th style={styles.th} align="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p._id} style={{background: selectedIds.has(p._id) ? '#f0f9ff' : 'transparent'}}>
                  <td style={styles.td}>
                    <input type="checkbox" checked={selectedIds.has(p._id)} onChange={() => toggleSelection(p._id)} />
                  </td>
                  <td style={styles.td}>
                    <img src={p.image || 'https://via.placeholder.com/48'} alt="thumb" style={styles.imgThumb} />
                  </td>
                  <td style={styles.td}>
                    <div style={{fontWeight: 'bold'}}>{p.title}</div>
                    <div style={{fontSize: '0.85rem', color: '#6b7280'}}>{p.brand} • {p.category}</div>
                    <a href={p.url} target="_blank" rel="noreferrer" style={{fontSize: '0.8rem', color: '#3b82f6'}}>View Source</a>
                  </td>
                  <td style={styles.td}>${p.price}</td>
                  <td style={styles.td}>
                    <span style={styles.pill(p.inventory_status)}>{p.inventory_status || 'N/A'}</span>
                  </td>
                  {/* ADD THIS NEW TD BLOCK */}
                  {/* REPLACING THE PREVIOUS FLAGS TD BLOCK */}
                  <td style={styles.td}>
                    {p.has_live_duplicate ? (
                      <span style={{
                        display: 'inline-block', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '6px', 
                        fontSize: '0.75rem', 
                        fontWeight: '600', 
                        background: '#fef2f2', // Lighter red
                        color: '#991b1b',      // Dark red text
                        border: '1px solid #f87171'
                      }}>
                        Live Duplicate!
                      </span>
                    ) : p.is_duplicate ? (
                      <span style={{
                        display: 'inline-block', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '6px', 
                        fontSize: '0.75rem', 
                        fontWeight: '600', 
                        background: '#fffbeb', // Light amber/yellow
                        color: '#92400e',      // Dark amber text
                        border: '1px solid #fcd34d'
                      }}>
                        Duplicate
                      </span>
                    ) : (
                      <span style={{color: '#9ca3af', fontSize: '0.85rem'}}>-</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {p.created_at ? new Date(p.created_at).toLocaleDateString(undefined, {
                       month: 'short', day: 'numeric', year: 'numeric' 
                    }) : 'N/A'}
                  </td>
                  <td style={styles.td}>
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString(undefined, {
                       month: 'short', day: 'numeric', year: 'numeric' 
                    }) : 'N/A'}
                  </td>
                  <td style={styles.td} align="right">
                    <button 
                      style={{...styles.btn, marginRight: '0.5rem', color: '#3b82f6'}} 
                      onClick={() => openEditModal(p.brand, p.title)}
                      disabled={isFetchingDetails}
                    >
                      {isFetchingDetails ? 'Loading...' : 'Edit'}
                    </button>
                    <button style={{...styles.btn, color: '#ef4444'}} onClick={() => handleDelete(p._id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Pagination */}
      <div style={{marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem'}}>
          <button disabled={pagination.page === 1} onClick={() => setPagination(p => ({...p, page: p.page - 1}))} style={styles.btn}>Previous</button>
          <span style={{alignSelf:'center'}}>Page {pagination.page} of {pagination.pages}</span>
          <button disabled={pagination.page >= pagination.pages} onClick={() => setPagination(p => ({...p, page: p.page + 1}))} style={styles.btn}>Next</button>
      </div>

      {/* Edit Modal */}
      {editItem && (
        <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) setEditItem(null) }}>
          <div style={styles.modal}>
            <h2 style={{marginTop: 0}}>{editItem._id ? 'Edit Product & Availability' : 'New Product'}</h2>
            <form onSubmit={handleSaveProduct}>
              
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem'}}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Brand</label>
                  <input 
                    style={{...styles.formInput, background: editItem._id ? '#f3f4f6' : '#fff', cursor: editItem._id ? 'not-allowed' : 'text'}} 
                    required 
                    disabled={!!editItem._id}
                    value={editItem.brand || ''} 
                    onChange={e => setEditItem({...editItem, brand: e.target.value})} 
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Current Price ($)</label>
                  <input type="number" step="0.01" style={styles.formInput} value={editItem.price || ''} onChange={e => setEditItem({...editItem, price: parseFloat(e.target.value)})} />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Orig. Price (Strikethrough)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    style={styles.formInput} 
                    value={editItem.original_price || ''} 
                    onChange={e => {
                       const val = e.target.value;
                       setEditItem({...editItem, original_price: val ? parseFloat(val) : null});
                    }}
                    placeholder="e.g. 50" 
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Title</label>
                <input 
                  style={{...styles.formInput, background: editItem._id ? '#f3f4f6' : '#fff', cursor: editItem._id ? 'not-allowed' : 'text'}} 
                  required 
                  disabled={!!editItem._id}
                  value={editItem.title || ''} 
                  onChange={e => setEditItem({...editItem, title: e.target.value})} 
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea 
                  style={styles.textarea} 
                  rows="3"
                  value={editItem.description || ''} 
                  onChange={e => setEditItem({...editItem, description: e.target.value})} 
                  placeholder="Enter product description here..."
                />
              </div>

              <div style={styles.formGroup}>
                  <label style={styles.label}>Image URL</label>
                  <input style={styles.formInput} value={editItem.image || ''} onChange={e => setEditItem({...editItem, image: e.target.value})} />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Generic Colors (comma separated)</label>
                  <input style={styles.formInput} placeholder="white, brown" value={Array.isArray(editItem.colors) ? editItem.colors.join(', ') : editItem.colors} onChange={e => setEditItem({...editItem, colors: e.target.value})} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Sizes (comma separated)</label>
                  <input style={styles.formInput} placeholder="XS, S, M, L" value={Array.isArray(editItem.sizes) ? editItem.sizes.join(', ') : editItem.sizes} onChange={e => setEditItem({...editItem, sizes: e.target.value})} />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Inventory Status</label>
                <select style={styles.formInput} value={editItem.inventory_status || 'staging'} onChange={e => setEditItem({...editItem, inventory_status: e.target.value})}>
                   <option value="staging">Staging</option>
                   <option value="live">Live</option>
                   <option value="out_of_stock">Out of Stock</option>
                   <option value="created_manually">Created Manually</option>
                </select>
              </div>

              {/* Specific Brand Color Angles Section */}
              <div style={{marginTop: '2rem'}}>
                <h3 style={{fontSize: '1.1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem'}}>Color Angles (Brand Specific)</h3>
                <p style={{fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem'}}>Specify the exact brand colors (e.g. "Winter Frost") and their respective image URLs.</p>
                
                {Object.keys(editItem.color_angles || {}).map(colorKey => (
                  <div key={colorKey} style={styles.anglesCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                      <strong style={{color: '#111827'}}>{colorKey}</strong>
                      <button 
                        type="button" 
                        style={{background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem'}}
                        onClick={() => handleRemoveColorAngle(colorKey)}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem'}}>
                      <div>
                        <label style={{...styles.label, fontSize: '0.75rem'}}>Front Image</label>
                        <input 
                          style={styles.formInput} 
                          value={editItem.color_angles[colorKey]?.front || ''} 
                          onChange={(e) => handleColorAngleChange(colorKey, 'front', e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={{...styles.label, fontSize: '0.75rem'}}>Side Image</label>
                        <input 
                          style={styles.formInput} 
                          value={editItem.color_angles[colorKey]?.side || ''} 
                          onChange={(e) => handleColorAngleChange(colorKey, 'side', e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={{...styles.label, fontSize: '0.75rem'}}>Back Image</label>
                        <input 
                          style={styles.formInput} 
                          value={editItem.color_angles[colorKey]?.back || ''} 
                          onChange={(e) => handleColorAngleChange(colorKey, 'back', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Input to add a new color angle dictionary key */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <input
                    style={{...styles.formInput, width: '250px'}}
                    placeholder="New brand color (e.g., Espresso)"
                    value={newAngleColor}
                    onChange={(e) => setNewAngleColor(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddColorAngle();
                      }
                    }}
                  />
                  <button
                    type="button"
                    style={{...styles.btn, ...styles.btnSecondary}}
                    onClick={handleAddColorAngle}
                  >
                    Add Color
                  </button>
                </div>

              </div>

              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem'}}>
                <button type="button" onClick={() => setEditItem(null)} style={{...styles.btn, ...styles.btnSecondary}}>Cancel</button>
                <button type="submit" style={{...styles.btn, ...styles.btnPrimary}}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default VADashboard;